import React from 'react';
import { useRoles } from '../hooks/useRoles';

const MOBILE_VIEWPORT_MAX = 760;
const INSPECTOR_STORAGE_KEY = 'ita.superAdminHoverInspector';

const LABEL_DATA_ATTRIBUTES = [
  'data-debug-label',
  'data-debug-name',
  'data-element',
  'data-element-name',
  'data-field',
  'data-field-name',
  'data-field-label',
  'data-modal',
  'data-modal-name',
  'data-card',
  'data-card-name',
  'data-section',
  'data-section-name',
  'data-testid',
  'title',
];

const DESCRIPTOR_DATA_ATTRIBUTES = [
  'data-component',
  'data-element',
  'data-element-name',
  'data-field',
  'data-field-name',
  'data-field-label',
  'data-modal',
  'data-modal-name',
  'data-card',
  'data-card-name',
  'data-section',
  'data-section-name',
  'data-testid',
];

function sanitizeText(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.replace(/\s+/g, ' ').trim();
  return trimmed || null;
}

function pickAttribute(element: HTMLElement, attributes: string[]): string | null {
  for (const attr of attributes) {
    const sanitized = sanitizeText(element.getAttribute(attr));
    if (sanitized) return sanitized;
  }
  return null;
}

function formatAttribute(attr: string, value: string | null): string | null {
  const sanitized = sanitizeText(value);
  if (!sanitized) return null;
  return `[${attr}=${JSON.stringify(sanitized)}]`;
}

function getAriaLabel(element: HTMLElement): string | null {
  const ariaLabel = sanitizeText(element.getAttribute('aria-label'));
  if (ariaLabel) return ariaLabel;

  const labelledBy = element.getAttribute('aria-labelledby');
  if (labelledBy && typeof document !== 'undefined') {
    const ids = labelledBy.split(/\s+/).filter(Boolean);
    const labels: string[] = [];
    for (const id of ids) {
      const node = document.getElementById(id);
      if (!node) continue;
      const text = sanitizeText(node.textContent || '');
      if (text) labels.push(text);
    }
    if (labels.length > 0) {
      return labels.join(' / ');
    }
  }

  const title = sanitizeText(element.getAttribute('title'));
  if (title) return title;

  const alt = sanitizeText(element.getAttribute('alt'));
  if (alt) return alt;

  return null;
}

function getControlLabel(element: HTMLElement): string | null {
  if (
    element instanceof HTMLInputElement ||
    element instanceof HTMLSelectElement ||
    element instanceof HTMLTextAreaElement
  ) {
    const associatedLabels = element.labels ? Array.from(element.labels) : [];
    const labelText = associatedLabels
      .map((label) => label.innerText || label.textContent || '')
      .map((text) => text.replace(/\s+/g, ' ').trim())
      .filter(Boolean);
    if (labelText.length > 0) return labelText.join(' / ');

    const placeholder = 'placeholder' in element ? sanitizeText(element.placeholder) : null;
    if (placeholder) return placeholder;

    const name = sanitizeText(element.name);
    if (name) return name;
  }

  return null;
}

function getTextSnippet(element: HTMLElement): string | null {
  const raw = element.innerText || element.textContent || '';
  const trimmed = raw.replace(/\s+/g, ' ').trim();
  if (!trimmed) return null;
  if (trimmed.length > 120) {
    return trimmed.slice(0, 117) + '…';
  }
  return trimmed;
}

function getElementDescriptor(element: HTMLElement): string {
  const tag = element.tagName.toLowerCase();
  const idPart = element.id ? `#${element.id}` : '';
  const classPart = element.classList.length
    ? Array.from(element.classList)
        .filter(Boolean)
        .slice(0, 3)
        .map((cls) => `.${cls}`)
        .join('')
    : '';
  const descriptorParts: string[] = [`${tag}${idPart}${classPart}`];

  const roleAttr = formatAttribute('role', element.getAttribute('role'));
  if (roleAttr) descriptorParts.push(roleAttr);

  const nameAttr = formatAttribute('name', element.getAttribute('name'));
  if (nameAttr) descriptorParts.push(nameAttr);

  for (const attr of DESCRIPTOR_DATA_ATTRIBUTES) {
    const formatted = formatAttribute(attr, element.getAttribute(attr));
    if (formatted) {
      descriptorParts.push(formatted);
    }
  }

  return descriptorParts.join(' ');
}

function getHierarchy(element: HTMLElement): string | null {
  const segments: string[] = [];
  let current: HTMLElement | null = element;
  let depth = 0;
  while (current && depth < 4) {
    segments.push(getElementDescriptor(current));
    current = current.parentElement;
    depth += 1;
  }
  if (segments.length <= 1) return null;
  return segments.reverse().join(' > ');
}

function describeElement(element: HTMLElement): string | null {
  const dataLabel = pickAttribute(element, LABEL_DATA_ATTRIBUTES);
  const ariaLabel = getAriaLabel(element);
  const controlLabel = getControlLabel(element);
  const textSnippet = getTextSnippet(element);
  const label = dataLabel || ariaLabel || controlLabel || textSnippet;
  const descriptor = getElementDescriptor(element);
  const hierarchy = getHierarchy(element);

  const lines: string[] = [];
  if (label && label !== descriptor) lines.push(label);
  lines.push(descriptor);
  if (hierarchy) lines.push(`Path: ${hierarchy}`);
  return lines.join('\n');
}

function isInspectorOptedIn(event?: MouseEvent): boolean {
  if (event?.altKey) return true;
  if (typeof window === 'undefined') return false;

  try {
    const stored =
      window.sessionStorage.getItem(INSPECTOR_STORAGE_KEY) ??
      window.localStorage.getItem(INSPECTOR_STORAGE_KEY);
    if (stored === '1' || stored === 'true') return true;
  } catch {
    // Browser storage can be unavailable in private or constrained contexts.
  }

  return false;
}

export default function SuperAdminHoverInspector(): null {
  const { isSuperAdmin, effectiveSuperAdmin } = useRoles();
  const inspectorEnabled = isSuperAdmin || effectiveSuperAdmin;
  const [isMobileViewport, setIsMobileViewport] = React.useState(() => {
    if (typeof window === 'undefined') {
      return true;
    }
    return window.innerWidth <= MOBILE_VIEWPORT_MAX;
  });
  const shouldEnableInspector = inspectorEnabled && !isMobileViewport;
  const tooltipRef = React.useRef<HTMLDivElement | null>(null);
  const activeTargetRef = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const updateViewportState = () => {
      setIsMobileViewport(window.innerWidth <= MOBILE_VIEWPORT_MAX);
    };

    updateViewportState();
    window.addEventListener('resize', updateViewportState);
    return () => {
      window.removeEventListener('resize', updateViewportState);
    };
  }, []);

  React.useEffect(() => {
    if (!shouldEnableInspector) {
      return;
    }
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return;
    }

    const tooltip = document.createElement('div');
    tooltip.setAttribute('data-superuser-hover', 'true');
    Object.assign(tooltip.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      zIndex: '2147483647',
      pointerEvents: 'none',
      background: 'rgba(15, 23, 42, 0.92)',
      color: '#f8fafc',
      fontSize: '11px',
      lineHeight: '1.4',
      fontFamily:
        'ui-monospace, SFMono-Regular, SFMono, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
      padding: '6px 8px',
      borderRadius: '4px',
      boxShadow: '0 4px 12px rgba(15, 23, 42, 0.35)',
      maxWidth: '340px',
      whiteSpace: 'pre-wrap',
      display: 'none',
      transform: 'translate3d(-9999px, -9999px, 0)',
    } as Partial<CSSStyleDeclaration>);
    document.body.appendChild(tooltip);
    tooltipRef.current = tooltip;

    const hideTooltip = () => {
      const tip = tooltipRef.current;
      if (tip) {
        tip.style.display = 'none';
        tip.textContent = '';
        tip.style.transform = 'translate3d(-9999px, -9999px, 0)';
      }
      activeTargetRef.current = null;
    };

    const updatePosition = (event: MouseEvent) => {
      const tip = tooltipRef.current;
      if (!tip || tip.style.display === 'none') return;
      const offset = 12;
      let x = event.clientX + offset;
      let y = event.clientY + offset;
      const rect = tip.getBoundingClientRect();
      const maxX = window.innerWidth - rect.width - 8;
      const maxY = window.innerHeight - rect.height - 8;
      if (x > maxX) x = Math.max(8, event.clientX - rect.width - offset);
      if (y > maxY) y = Math.max(8, event.clientY - rect.height - offset);
      tip.style.left = `${Math.round(x)}px`;
      tip.style.top = `${Math.round(y)}px`;
      tip.style.transform = 'translate3d(0, 0, 0)';
    };

    const handleHover = (event: MouseEvent) => {
      if (!isInspectorOptedIn(event)) {
        hideTooltip();
        return;
      }

      const rawTarget = event.target;
      if (!(rawTarget instanceof Element)) {
        hideTooltip();
        return;
      }
      let target: HTMLElement | null = rawTarget instanceof HTMLElement ? rawTarget : null;
      if (!target) {
        let current: Element | null = rawTarget;
        while (current && !(current instanceof HTMLElement)) {
          current = current.parentElement;
        }
        target = current instanceof HTMLElement ? current : null;
      }
      if (!target) {
        hideTooltip();
        return;
      }
      if (tooltipRef.current && (target === tooltipRef.current || tooltipRef.current.contains(target))) {
        return;
      }
      if (target === document.body || target === document.documentElement) {
        hideTooltip();
        return;
      }
      if (!document.body.contains(target)) {
        hideTooltip();
        return;
      }
      if (activeTargetRef.current === target) {
        updatePosition(event);
        return;
      }

      const description = describeElement(target);
      if (!description) {
        hideTooltip();
        return;
      }
      activeTargetRef.current = target;
      const tip = tooltipRef.current;
      if (!tip) return;
      tip.textContent = description;
      tip.style.display = 'block';
      updatePosition(event);
    };

    const handleMove = (event: MouseEvent) => {
      if (!isInspectorOptedIn(event)) {
        hideTooltip();
        return;
      }

      const current = activeTargetRef.current;
      if (current && !document.body.contains(current)) {
        hideTooltip();
        return;
      }
      updatePosition(event);
    };

    const handleScroll = () => {
      const current = activeTargetRef.current;
      if (!current) return;
      if (!document.body.contains(current)) hideTooltip();
    };

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        hideTooltip();
      }
    };

    document.addEventListener('mouseover', handleHover, true);
    document.addEventListener('mousemove', handleMove, true);
    document.addEventListener('scroll', handleScroll, true);
    window.addEventListener('keydown', handleKey);

    return () => {
      document.removeEventListener('mouseover', handleHover, true);
      document.removeEventListener('mousemove', handleMove, true);
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('keydown', handleKey);
      hideTooltip();
      if (tooltipRef.current) {
        tooltipRef.current.remove();
        tooltipRef.current = null;
      }
    };
  }, [shouldEnableInspector]);

  return null;
}
